import React, { useState, useEffect } from 'react';
import Layout from '../components/dashboard/Layout';
import { invoicesAPI } from '../services/api';
import { 
  FileText, 
  Download, 
  DollarSign, 
  Calendar,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';

const InvoicesPageNew = () => {
  const [invoices, setInvoices] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'reminders'
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [invoicesRes, remindersRes] = await Promise.all([
        invoicesAPI.getAll(),
        invoicesAPI.getReminders()
      ]);
      setInvoices(invoicesRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId, invoiceType) => {
    setDownloading(invoiceId);
    try {
      const endpoint = invoiceType === 'sale' 
        ? `/invoices/download-sale/${invoiceId}`
        : `/invoices/download-rental/${invoiceId}`;
      
      await invoicesAPI.downloadInvoice(invoiceId, invoiceType);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Gagal download invoice: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDownloading(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-muted text-muted-foreground border-border',
      pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
      sent: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      paid: 'bg-green-500/10 text-green-600 border-green-500/30',
      overdue: 'bg-red-500/10 text-red-600 border-red-500/30',
      cancelled: 'bg-gray-500/10 text-gray-600 border-gray-500/30'
    };
    return colors[status] || colors.draft;
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      overdue: 'bg-red-500/10 text-red-600 border-red-500/30',
      today: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
      upcoming: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30'
    };
    return colors[urgency] || colors.upcoming;
  };

  const getUrgencyIcon = (urgency) => {
    if (urgency === 'overdue') return <AlertCircle className="w-5 h-5 text-red-600" />;
    if (urgency === 'today') return <Clock className="w-5 h-5 text-orange-600" />;
    return <Calendar className="w-5 h-5 text-yellow-600" />;
  };

  // Stats
  const stats = {
    total: invoices.length,
    pending: invoices.filter(i => i.status === 'pending').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: reminders.filter(r => r.urgency === 'overdue').length
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <RefreshCw className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading invoices...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">💰 Invoice & Reminder</h1>
            <p className="text-muted-foreground">Generate invoice dan tracking pembayaran</p>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass-card p-4 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Invoice</p>
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
          </div>
          
          <div className="glass-card p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-yellow-600">Pending</p>
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          
          <div className="glass-card p-4 rounded-xl border border-green-500/30 bg-green-500/5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-green-600">Paid</p>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
          </div>
          
          <div className="glass-card p-4 rounded-xl border border-red-500/30 bg-red-500/5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-red-600">Overdue</p>
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="glass-card p-2 rounded-xl border border-border/40">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              📄 Semua Invoice ({stats.total})
            </button>
            <button
              onClick={() => setActiveTab('reminders')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'reminders'
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              ⏰ Reminder Pembayaran ({reminders.length})
            </button>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'all' ? (
          /* All Invoices */
          <div className="space-y-4">
            {invoices.length === 0 ? (
              <div className="glass-card p-12 rounded-xl border border-border/40 text-center">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Belum Ada Invoice</h3>
                <p className="text-muted-foreground">Invoice akan otomatis dibuat saat rental atau penjualan</p>
              </div>
            ) : (
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="glass-card p-6 rounded-xl border border-border/40 hover:border-primary/30 transition-all"
                >
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold text-foreground">
                          {invoice.invoice_number}
                        </h3>
                        <span className={`px-3 py-1 rounded text-xs font-medium border ${getStatusColor(invoice.status)}`}>
                          {invoice.status.toUpperCase()}
                        </span>
                        {invoice.invoice_type && (
                          <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs">
                            {invoice.invoice_type === 'sale' ? '💰 Sale' : '📦 Rental'}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Client:</p>
                          <p className="text-foreground font-medium">{invoice.client_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Asset:</p>
                          <p className="text-foreground font-medium">{invoice.asset_info || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Amount:</p>
                          <p className="text-foreground font-bold">Rp {(invoice.total_amount || 0).toLocaleString('id-ID')}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Due Date:</p>
                          <p className="text-foreground">
                            {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('id-ID') : 'Paid'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_type)}
                      disabled={downloading === invoice.id}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      {downloading === invoice.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Payment Reminders */
          <div className="space-y-4">
            {reminders.length === 0 ? (
              <div className="glass-card p-12 rounded-xl border border-border/40 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Tidak Ada Reminder</h3>
                <p className="text-muted-foreground">Semua invoice dalam kondisi baik!</p>
              </div>
            ) : (
              reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`glass-card p-6 rounded-xl border-2 ${getUrgencyColor(reminder.urgency).replace('bg-', 'border-').replace('/10', '/40')}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getUrgencyIcon(reminder.urgency)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-foreground">
                          {reminder.invoice_number}
                        </h3>
                        <span className={`px-3 py-1 rounded-lg text-sm font-bold border-2 ${getUrgencyColor(reminder.urgency)}`}>
                          {reminder.days_message}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Client</p>
                          <p className="text-foreground font-medium">{reminder.client_name}</p>
                          <p className="text-sm text-muted-foreground">{reminder.client_phone}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Asset</p>
                          <p className="text-foreground font-medium">{reminder.asset_info}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Amount</p>
                          <p className="text-foreground font-bold text-lg">
                            Rp {(reminder.amount || 0).toLocaleString('id-ID')}
                          </p>
                        </div>
                      </div>
                      
                      {reminder.urgency === 'overdue' && (
                        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <p className="text-sm text-red-600 font-medium">
                            ⚠️ Invoice ini sudah terlambat! Segera hubungi client untuk follow up pembayaran.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleDownloadInvoice(reminder.id, reminder.invoice_type)}
                      disabled={downloading === reminder.id}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      {downloading === reminder.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InvoicesPageNew;